import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, AfterViewChecked, ChangeDetectionStrategy, inject, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxMapboxGLModule, MapComponent } from 'ngx-mapbox-gl';
import { Subject, takeUntil } from 'rxjs';
import { PdfExportContextService } from '../../core/services/pdf-export-context.service';
import { PdfExportDataService } from '../../core/services/pdf-export-data.service';
import { environment } from '../../../environments/environment';
import * as QRCode from 'qrcode';

declare global {
  interface Window {
    PERARE_POLYGON_LOADED: boolean;
  }
}

@Component({
  selector: 'app-polygon-print',
  standalone: true,
  imports: [CommonModule, NgxMapboxGLModule],
  templateUrl: './polygon-print.component.html',
  styleUrl: './polygon-print.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PolygonPrintComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  private destroy$ = new Subject<void>();
  private pdfContext = inject(PdfExportContextService);
  private pdfExportDataService = inject(PdfExportDataService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  isLoading = true;
  hasData = false;
  errorMessage: string | null = null;
  readonly mapboxToken = environment.mapboxToken;

  polygonCoordinates: [number, number][] | null = null;
  isochroneData: any | null = null;
  polygonBounds: [[number, number], [number, number]] | undefined = undefined;
  mapCenter: [number, number] = [106.828842, -6.175908]; // Default center
  qrCodeDataUrl: string | null = null;

  @ViewChild(MapComponent) mapComponent?: MapComponent;
  private mapSubscribed = false;

  ngOnInit(): void {
    // Set data-only mode
    this.pdfContext.setPdfMode(true);
    this.pdfContext.setDataOnlyMode(true);

    // Load polygon data from sessionStorage
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const sessionId = params['sessionId'];
      if (sessionId) {
        this.loadPolygonData(sessionId);
      } else {
        this.isLoading = false;
        this.hasData = false;
        this.errorMessage = 'Session ID not provided';
        this.cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit(): void {
    // Component view initialized
  }

  ngAfterViewChecked(): void {
    // Check if map component became available and subscribe to it only once
    if (this.mapComponent && !this.mapSubscribed) {
      this.mapSubscribed = true;
      this.mapComponent.mapLoad.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.onMapLoad();
      });

      // Also listen for map creation to check for WebGL issues
      this.mapComponent.mapCreate.pipe(takeUntil(this.destroy$)).subscribe((map: any) => {
        console.log('[PolygonPrint] Map created');
        this.checkMapCapabilities(map);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load polygon data from sessionStorage
   */
  private loadPolygonData(sessionId: string): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const polygonData = this.pdfExportDataService.getPolygonData(sessionId);

    if (!polygonData || !polygonData.coordinates || polygonData.coordinates.length === 0) {
      this.isLoading = false;
      this.hasData = false;
      this.errorMessage = 'Polygon data not found. Please try exporting again.';
      this.cdr.markForCheck();
      return;
    }

    this.polygonCoordinates = polygonData.coordinates;
    this.isochroneData = polygonData.isochrone || null;
    this.calculateBounds();
    this.calculateCenter();

    // Generate QR code if URL is provided
    if (polygonData.url) {
      this.generateQrCode(polygonData.url);
    }

    this.isLoading = false;
    this.hasData = true;
    this.errorMessage = null;

    // Don't set flag immediately, wait for map to fully load in onMapLoad

    this.cdr.markForCheck();
  }

  /**
   * Calculate bounds from polygon coordinates
   */
  private calculateBounds(): void {
    if (!this.polygonCoordinates || this.polygonCoordinates.length === 0) {
      this.polygonBounds = undefined;
      return;
    }

    const allLngs: number[] = [];
    const allLats: number[] = [];

    this.polygonCoordinates.forEach(coord => {
      allLngs.push(coord[0]);
      allLats.push(coord[1]);
    });

    if (allLngs.length === 0 || allLats.length === 0) {
      this.polygonBounds = undefined;
      return;
    }

    this.polygonBounds = [
      [Math.min(...allLngs), Math.min(...allLats)],  // Southwest corner
      [Math.max(...allLngs), Math.max(...allLats)]   // Northeast corner
    ];
  }

  /**
   * Calculate center point from polygon coordinates
   */
  private calculateCenter(): void {
    if (!this.polygonCoordinates || this.polygonCoordinates.length === 0) {
      return;
    }

    const allLngs: number[] = [];
    const allLats: number[] = [];

    this.polygonCoordinates.forEach(coord => {
      allLngs.push(coord[0]);
      allLats.push(coord[1]);
    });

    if (allLngs.length === 0 || allLats.length === 0) {
      return;
    }

    const centerLng = (Math.min(...allLngs) + Math.max(...allLngs)) / 2;
    const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;

    this.mapCenter = [centerLng, centerLat];
  }

  /**
   * Get polygon GeoJSON for map display
   */
  get polygonGeoJson() {
    if (!this.polygonCoordinates || this.polygonCoordinates.length === 0) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [this.polygonCoordinates]
          }
        }
      ]
    };
  }

  /**
   * Handle map load event to apply fitBounds
   */
  onMapLoad(): void {
    if (this.polygonBounds && this.mapComponent?.mapInstance) {
      const map = this.mapComponent.mapInstance;

      setTimeout(() => {
        map.fitBounds(this.polygonBounds as any, {
          padding: 50,
          maxZoom: 18,
          duration: 0 // Instant for PDF export
        });

        //note: [SHR]: trying to force render
        map.resize();
        map.triggerRepaint();

        console.log('[PolygonPrint] Map bounds fitted, forced repaint');

        setTimeout(() => {
          if (map.isStyleLoaded() && map.areTilesLoaded()) {
            console.log('[PolygonPrint] Map fully loaded with tiles');
            this.setMapLoadedFlag();
          } else {
            this.waitForMapFullyLoaded(map);
          }
        }, 1000);
      }, 100);
    }
  }

  private checkMapCapabilities(map: any): void {
    try {
      const canvas = map.getCanvas();
      const gl = canvas?.getContext('webgl') || canvas?.getContext('webgl2') || canvas?.getContext('experimental-webgl');

      if (!gl) {
        console.error('[PolygonPrint] WebGL context not available');
        this.errorMessage = 'WebGL not available for map rendering';
        this.cdr.markForCheck();
        return;
      }

      console.log('[PolygonPrint] WebGL context available', {
        vendor: gl.getParameter(gl.VENDOR),
        renderer: gl.getParameter(gl.RENDERER),
        version: gl.getParameter(gl.VERSION),
      });
    } catch (error) {
      console.error('[PolygonPrint] Error checking WebGL capabilities:', error);
    }
  }


  private waitForMapFullyLoaded(map: any): void {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max

    const checkInterval = setInterval(() => {
      attempts++;

      if (map.isStyleLoaded() && map.areTilesLoaded()) {
        console.log('[PolygonPrint] Map fully loaded after', attempts * 100, 'ms');
        clearInterval(checkInterval);
        this.setMapLoadedFlag();
      } else if (attempts >= maxAttempts) {
        console.warn('[PolygonPrint] Map loading timeout after 5s');
        clearInterval(checkInterval);
        this.setMapLoadedFlag();
      }
    }, 100);
  }

  //note: [SHR] just here set flag, not immediately as before
  private setMapLoadedFlag(): void {
    if (typeof window !== 'undefined') {
      window.PERARE_POLYGON_LOADED = true;
      console.log('[PolygonPrint] PERARE_POLYGON_LOADED flag set');
    }
  }

  /**
   * Generate QR code from URL
   */
  private async generateQrCode(url: string): Promise<void> {
    try {
      this.qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        errorCorrectionLevel: 'M'
      });
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      this.qrCodeDataUrl = null;
    }
  }
}
